import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const initialForm = {
  campaign_name: '',
  audience: 'all_users',
  start_date: '',
  end_date: '',
  notification_title: '',
  notification_body: '',
  promo_code_id: '',
};

const audienceLabels = {
  all_users: { label: 'All Users', icon: 'Users', color: 'text-blue-600 bg-blue-50' },
  drivers: { label: 'Drivers Only', icon: 'Car', color: 'text-green-600 bg-green-50' },
  customers: { label: 'Customers Only', icon: 'User', color: 'text-purple-600 bg-purple-50' },
  new_users: { label: 'New Users', icon: 'UserPlus', color: 'text-amber-600 bg-amber-50' },
};

const statusConfig = {
  draft: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Draft' },
  scheduled: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Scheduled' },
  active: { bg: 'bg-green-100', text: 'text-green-700', label: 'Active' },
  completed: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Completed' },
  cancelled: { bg: 'bg-red-100', text: 'text-red-600', label: 'Cancelled' },
};

const CampaignsPanel = () => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [successMsg, setSuccessMsg] = useState('');
  const [preview, setPreview] = useState(false);
  const [toast, setToast] = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const fetchData = useCallback(async () => {
    setFetchLoading(true);
    try {
      const [campRes, promoRes] = await Promise.all([
        supabase?.from('campaigns')?.select('*')?.order('created_at', { ascending: false }),
        supabase?.from('promo_codes')?.select('id, code_name')?.eq('promo_status', 'active'),
      ]);
      setCampaigns(campRes?.data || []);
      setPromoCodes(promoRes?.data || []);
    } catch (err) {
      console.error('Failed to fetch campaigns:', err);
    } finally {
      setFetchLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const validate = () => {
    const errs = {};
    if (!form?.campaign_name?.trim()) errs.campaign_name = 'Campaign name is required';
    if (!form?.start_date) errs.start_date = 'Start date is required';
    if (!form?.end_date) errs.end_date = 'End date is required';
    if (form?.start_date && form?.end_date && new Date(form?.end_date) <= new Date(form?.start_date)) errs.end_date = 'End date must be after start date';
    if (!form?.notification_title?.trim()) errs.notification_title = 'Notification title is required';
    if (!form?.notification_body?.trim()) errs.notification_body = 'Notification message is required';
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const errs = validate();
    if (Object.keys(errs)?.length > 0) { setErrors(errs); return; }
    setLoading(true);
    try {
      const now = new Date();
      const startDate = new Date(form?.start_date);
      const status = startDate <= now ? 'active' : 'scheduled';
      const { error } = await supabase?.from('campaigns')?.insert({
        campaign_name: form?.campaign_name?.trim(),
        audience: form?.audience,
        start_date: new Date(form?.start_date)?.toISOString(),
        end_date: new Date(form?.end_date)?.toISOString(),
        notification_title: form?.notification_title?.trim(),
        notification_body: form?.notification_body?.trim(),
        promo_code_id: form?.promo_code_id || null,
        campaign_status: status,
      });
      if (error) throw error;
      setSuccessMsg('Campaign created successfully!');
      setForm(initialForm);
      setErrors({});
      setPreview(false);
      fetchData();
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      setErrors({ submit: err?.message || 'Failed to create campaign' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCampaign = async (id, name) => {
    if (!window.confirm(`Delete campaign "${name}"?`)) return;
    try {
      const { error } = await supabase?.from('campaigns')?.delete()?.eq('id', id);
      if (error) throw error;
      showToast(`Campaign "${name}" deleted`);
      fetchData();
    } catch (err) {
      showToast(`Error: ${err?.message}`);
    }
  };

  return (
    <div className="space-y-6">
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-gray-900 text-white text-sm px-4 py-2.5 rounded-xl shadow-lg">
          {toast}
        </div>
      )}
      {/* Create Campaign Form */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3" style={{ background: 'linear-gradient(135deg, #0f2044 0%, #1a3a6e 100%)' }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(212,175,55,0.2)' }}>
            <Icon name="Megaphone" size={18} color="#D4AF37" />
          </div>
          <h2 className="text-lg font-semibold text-white">Create Campaign</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {successMsg && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">
              <Icon name="CheckCircle" size={16} />
              <span>{successMsg}</span>
            </div>
          )}
          {errors?.submit && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              <Icon name="AlertCircle" size={16} />
              <span>{errors?.submit}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Campaign Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Campaign Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="campaign_name"
                value={form?.campaign_name}
                onChange={handleChange}
                placeholder="e.g. Weekend Ride Blitz"
                className={`w-full px-4 py-2.5 rounded-xl border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors?.campaign_name ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'}`}
              />
              {errors?.campaign_name && <p className="mt-1 text-xs text-red-500">{errors?.campaign_name}</p>}
            </div>

            {/* Audience */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Target Audience</label>
              <select
                name="audience"
                value={form?.audience}
                onChange={handleChange}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-colors"
              >
                {Object.entries(audienceLabels)?.map(([val, cfg]) => (
                  <option key={val} value={val}>{cfg?.label}</option>
                ))}
              </select>
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Start Date <span className="text-red-500">*</span></label>
              <input
                type="datetime-local"
                name="start_date"
                value={form?.start_date}
                onChange={handleChange}
                className={`w-full px-4 py-2.5 rounded-xl border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors?.start_date ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'}`}
              />
              {errors?.start_date && <p className="mt-1 text-xs text-red-500">{errors?.start_date}</p>}
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">End Date <span className="text-red-500">*</span></label>
              <input
                type="datetime-local"
                name="end_date"
                value={form?.end_date}
                onChange={handleChange}
                className={`w-full px-4 py-2.5 rounded-xl border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors?.end_date ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'}`}
              />
              {errors?.end_date && <p className="mt-1 text-xs text-red-500">{errors?.end_date}</p>}
            </div>
          </div>

          {/* Promo Code Link */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Link Promo Code (optional)</label>
            <select
              name="promo_code_id"
              value={form?.promo_code_id}
              onChange={handleChange}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-colors"
            >
              <option value="">No promo code</option>
              {promoCodes?.map(p => (
                <option key={p?.id} value={p?.id}>{p?.code_name}</option>
              ))}
            </select>
          </div>

          {/* Notification Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Push Notification Title <span className="text-red-500">*</span></label>
            <input
              type="text"
              name="notification_title"
              value={form?.notification_title}
              onChange={handleChange}
              placeholder="e.g. Weekend Special!"
              maxLength={60}
              className={`w-full px-4 py-2.5 rounded-xl border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors?.notification_title ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'}`}
            />
            <div className="flex justify-between mt-1">
              {errors?.notification_title ? <p className="text-xs text-red-500">{errors?.notification_title}</p> : <span />}
              <span className="text-xs text-gray-400">{form?.notification_title?.length}/60</span>
            </div>
          </div>

          {/* Notification Body */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Push Notification Message <span className="text-red-500">*</span></label>
            <textarea
              name="notification_body"
              value={form?.notification_body}
              onChange={handleChange}
              placeholder="Write your promotional message here..."
              rows={3}
              maxLength={160}
              className={`w-full px-4 py-2.5 rounded-xl border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${errors?.notification_body ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'}`}
            />
            <div className="flex justify-between mt-1">
              {errors?.notification_body ? <p className="text-xs text-red-500">{errors?.notification_body}</p> : <span />}
              <span className="text-xs text-gray-400">{form?.notification_body?.length}/160</span>
            </div>
          </div>

          {/* Preview Toggle */}
          {form?.notification_title && form?.notification_body && (
            <div>
              <button
                type="button"
                onClick={() => setPreview(p => !p)}
                className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
              >
                <Icon name={preview ? 'EyeOff' : 'Eye'} size={14} />
                {preview ? 'Hide Preview' : 'Preview Notification'}
              </button>
              {preview && (
                <div className="mt-3 p-4 bg-gray-900 rounded-2xl max-w-xs shadow-xl">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#D4AF37' }}>
                      <Icon name="Zap" size={14} color="white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-xs font-semibold truncate">{form?.notification_title || 'Title'}</p>
                      <p className="text-gray-300 text-xs mt-0.5 leading-relaxed line-clamp-2">{form?.notification_body || 'Message'}</p>
                    </div>
                  </div>
                  <p className="text-gray-500 text-xs mt-2 text-right">now</p>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 rounded-xl text-sm font-semibold text-white transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ background: loading ? '#6b7280' : 'linear-gradient(135deg, #0f2044 0%, #1a3a6e 100%)' }}
            >
              {loading ? (
                <><Icon name="Loader2" size={16} className="animate-spin" /> Creating...</>
              ) : (
                <><Icon name="Send" size={16} /> Launch Campaign</>
              )}
            </button>
          </div>
        </form>
      </div>
      {/* Campaigns List */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-base font-semibold text-gray-900">All Campaigns</h3>
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">{campaigns?.length}</span>
          </div>
          <button onClick={fetchData} className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors">
            <Icon name="RefreshCw" size={14} className="text-gray-600" />
          </button>
        </div>

        {fetchLoading ? (
          <div className="flex items-center justify-center py-12">
            <Icon name="Loader2" size={24} className="animate-spin text-gray-400" />
          </div>
        ) : campaigns?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            <Icon name="Megaphone" size={32} className="text-gray-300" />
            <p className="text-sm text-gray-500">No campaigns yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {campaigns?.map(campaign => {
              const aud = audienceLabels?.[campaign?.audience] || audienceLabels?.all_users;
              const sc = statusConfig?.[campaign?.campaign_status] || statusConfig?.draft;
              return (
                <div key={campaign?.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-sm font-semibold text-gray-900">{campaign?.campaign_name}</h4>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${sc?.bg} ${sc?.text}`}>
                          {sc?.label}
                        </span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${aud?.color}`}>
                          <Icon name={aud?.icon} size={10} />
                          {aud?.label}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-1">{campaign?.notification_body}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <Icon name="Calendar" size={10} />
                          {new Date(campaign?.start_date)?.toLocaleDateString()} – {new Date(campaign?.end_date)?.toLocaleDateString()}
                        </span>
                        {campaign?.sent_count > 0 && (
                          <span className="flex items-center gap-1">
                            <Icon name="Send" size={10} />
                            {campaign?.sent_count?.toLocaleString()} sent
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteCampaign(campaign?.id, campaign?.campaign_name)}
                      className="p-1.5 rounded-lg hover:bg-red-50 transition-colors flex-shrink-0"
                    >
                      <Icon name="Trash2" size={14} className="text-red-400" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignsPanel;
